/**
 * Tooltip pro Graph2 — extrahováno mimo komponent, aby se nevytvářel během render.
 *
 * Dva režimy (bez UA `isMobile`):
 *   - `showTooltipAt` == null → recharts hover tooltip (jemný pointer): relativní pozice.
 *   - `showTooltipAt` != null → plovoucí karta (touch tap / long-press scrub): `position:fixed`
 *     u prstu, vždy clamped/flipnutá do viewportu (nahrazuje původní `top = clientY-100`,
 *     která ujížděla mimo obrazovku).
 *
 * Interpolace per-topic: každý topic má vlastní sparse data (různý sampling rate).
 * Když cursor hover na ts kde JEDEN topic má bod (např. meteo @ 06:00 každou minutu),
 * ostatní topic musí najít vlastní prev/next bod (kuchyně možná měří jen každou hodinu).
 * Per-topic binary search najde nejbližší prev/next (viz CHART-DATA-REFACTOR-FIX Fáze 9).
 */

import React from "react";
import { formatDate } from "@globals/format";
import { mathRound } from "@globals/numbers";
import { colors, suspiciousColor } from "@client/constants";
import type { ChartPoint } from "@client/store/chart";
import { nickname } from "@client/utils/nickname";
import { unit, unundefined } from "@client/utils/values";

/** Odhad rozměrů karty pro viewport clamp (bez měření DOM). */
const TOOLTIP_WIDTH = 220;
const TOOLTIP_ROW_H = 20;
const TOOLTIP_HEADER_H = 30;
const VIEWPORT_PAD = 8;
/** Vertikální mezera mezi prstem a kartou (aby karta nebyla pod prstem). */
const FINGER_GAP = 18;

const findClosestInPoints = (
  points: ChartPoint[],
  target: number
): { prev: ChartPoint | null; next: ChartPoint | null } => {
  if (points.length === 0) return { prev: null, next: null };
  let left = 0;
  let right = points.length - 1;
  while (left <= right) {
    const mid = (left + right) >>> 1;
    if (points[mid].ts === target) return { prev: points[mid], next: points[mid] };
    if (points[mid].ts < target) left = mid + 1;
    else right = mid - 1;
  }
  return { prev: points[right] ?? null, next: points[left] ?? null };
};

const toNumber = (v: number | boolean | null | undefined): number | undefined => {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  return undefined;
};

const interpolate = (
  prev: { ts: number; v: number | boolean | null } | null,
  next: { ts: number; v: number | boolean | null } | null,
  target: number
): number | undefined => {
  const prevV = toNumber(prev?.v);
  const nextV = toNumber(next?.v);
  if (prevV === undefined) return nextV;
  if (nextV === undefined) return prevV;
  if (!prev || !next || prev.ts === next.ts) return prevV;
  const ratio = (target - prev.ts) / (next.ts - prev.ts);
  return prevV + (nextV - prevV) * ratio;
};

/** Spočítá `position:fixed` styl plovoucí karty tak, aby zůstala ve viewportu. */
const clampedFloatingStyle = (
  clientX: number,
  clientY: number,
  rowCount: number
): React.CSSProperties => {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const height = TOOLTIP_HEADER_H + rowCount * TOOLTIP_ROW_H;

  // Horizontální: karta je centrovaná na X (translateX(-50%)); clamp střed tak,
  // aby se karta vešla s odsazením od krajů.
  const half = TOOLTIP_WIDTH / 2;
  const left = Math.min(Math.max(clientX, half + VIEWPORT_PAD), vw - half - VIEWPORT_PAD);

  // Vertikální: preferuj nad prstem; když se nevejde, dej pod prst; pak clamp.
  let top = clientY - height - FINGER_GAP;
  if (top < VIEWPORT_PAD) top = clientY + FINGER_GAP;
  top = Math.min(top, vh - height - VIEWPORT_PAD);
  top = Math.max(top, VIEWPORT_PAD);

  return {
    position: "fixed",
    left,
    top,
    transform: "translateX(-50%)",
    maxWidth: TOOLTIP_WIDTH,
    pointerEvents: "none",
  };
};

interface Graph2TooltipProps {
  active?: boolean;
  label?: number;
  dark: boolean;
  showTooltipAt: { x: number; y: number; timestamp: number } | null;
  topicIds: string[];
  topics: Record<string, MQTTValue["topic"]>;
  /** Per-topic sorted points (z useChartData.series). Klíč topic, hodnota sorted ASC by ts. */
  seriesByTopic: Map<string, ChartPoint[]>;
  suspicious: Record<string, boolean>;
}

export const Graph2Tooltip: React.FC<Graph2TooltipProps> = ({
  active,
  label,
  dark,
  showTooltipAt,
  topicIds,
  topics,
  seriesByTopic,
  suspicious,
}) => {
  const floating = showTooltipAt != null;
  const effectiveLabel = floating ? showTooltipAt.timestamp : label;
  const effectiveActive = floating ? true : active;
  if (!effectiveActive || effectiveLabel === undefined) return null;

  const floatingStyle = floating
    ? clampedFloatingStyle(showTooltipAt.x, showTooltipAt.y, topicIds.length)
    : null;

  return (
    <div
      style={{
        backgroundColor: dark ? "#000000" : "#ffffff",
        color: dark ? "#ffffff" : "#000000",
        padding: "10px",
        border: "1px solid #ccc",
        borderRadius: "5px",
        zIndex: 1000,
        ...(floatingStyle ?? { position: "relative" }),
      }}
    >
      <p>{formatDate(new Date(effectiveLabel), "d. M. yyyy HH:mm:ss")}</p>
      {topicIds.map((topic, index) => {
        const points = seriesByTopic.get(topic) ?? [];
        const { prev, next } = findClosestInPoints(points, effectiveLabel);
        const value = interpolate(prev, next, effectiveLabel);
        return (
          <p
            key={topic}
            style={{
              color: suspicious[topic] ? suspiciousColor : colors[index % colors.length],
              margin: "0",
            }}
          >
            {nickname(topics[topic])}:{" "}
            {topics[topic]?.valueType === "BOOLEAN"
              ? unundefined(value, "---")
              : (unit(
                  unundefined(
                    value !== undefined ? mathRound(value, topics[topic]?.decimals ?? 1) : undefined,
                    "---"
                  ),
                  topics[topic]?.unit ?? ""
                ) ?? "N/A")}
          </p>
        );
      })}
    </div>
  );
};
